/**
 * Tolerância do rateio da compra (ADR 0094): diferença de até 10% e até R$ 1.000 segue sozinha, com
 * aviso ao gestor do Centro de Custo Master e a quem pediu; acima de qualquer um dos dois, volta à
 * aprovação. Vale para a mudança de preço de um valor fixo e para a mudança do rateio depois de
 * aprovado. A aprovação da contratação pelo valor total não muda. Em outra moeda, o limite é o valor
 * convertido pela cotação da plataforma e arredondado ao valor inteiro.
 */
import { roundToCurrency } from './fpa-despesas.ts';
import { fromScaled, toScaled } from './money.ts';

export const SPLIT_TOLERANCE_PERCENT = '10';
export const SPLIT_TOLERANCE_AMOUNT_BRL = '1000';

const HUNDRED = toScaled('100');

/** Rateio de um item num momento: percentuais por centro e, no valor fixo, o total informado. */
export interface SplitSnapshotItem {
  lines: { costCenterId: string; percent: string | number }[];
  amount: string | number | null;
}
export type SplitSnapshot = Record<string, SplitSnapshotItem>;

export interface SplitChange {
  /** Quanto mudou: valor que trocou de centro mais a diferença de preço do valor fixo. */
  changedAmount: string;
  /** O valor de referência da compra, para o percentual. */
  baseAmount: string;
  changed: boolean;
}

const scaled = (v: string | number) => toScaled(String(v));

/** Percentual que saiu de um centro para outro entre os dois retratos. */
function movedPercent(before: SplitSnapshotItem | undefined, after: SplitSnapshotItem): bigint {
  if (!before) return HUNDRED;
  const was = new Map(before.lines.map((l) => [l.costCenterId, scaled(l.percent)]));
  return after.lines.reduce((sum, l) => {
    const gain = scaled(l.percent) - (was.get(l.costCenterId) ?? 0n);
    return gain > 0n ? sum + gain : sum;
  }, 0n);
}

/** Quanto mudou desde a aprovação, na moeda da proposta. `items` traz o total de cada item nela. */
export function splitChange(
  approved: SplitSnapshot,
  current: SplitSnapshot,
  items: readonly { itemId: string; total: string }[],
): SplitChange {
  let changed = 0n;
  let base = 0n;
  for (const { itemId, total } of items) {
    const after = current[itemId];
    if (!after) continue;
    const before = approved[itemId];
    const price = scaled(total);
    // Só o valor fixo tem um total aprovado para comparar; no percentual, vale a proporção.
    const reference = before?.amount != null ? scaled(before.amount) : price;
    const difference = price > reference ? price - reference : reference - price;
    changed += difference + (price * movedPercent(before, after)) / HUNDRED;
    base += reference;
  }
  return {
    changedAmount: fromScaled(changed),
    baseAmount: fromScaled(base),
    changed: changed > 0n,
  };
}

/** Os R$ 1.000 já convertidos para a moeda da proposta: arredonda ao valor inteiro. */
export function roundSplitToleranceLimit(converted: string): string {
  return fromScaled(roundToCurrency(toScaled(converted), 0));
}

/**
 * Dentro da tolerância: até 10% do valor de referência e até o limite, na moeda da proposta. Sem
 * limite (`amountLimit` nulo, falta a cotação), não dá para garantir: volta à aprovação.
 */
export function withinSplitTolerance(change: SplitChange, amountLimit: string | null): boolean {
  if (amountLimit == null) return false;
  const limit = (toScaled(change.baseAmount) * toScaled(SPLIT_TOLERANCE_PERCENT)) / HUNDRED;
  return (
    toScaled(change.changedAmount) <= limit &&
    toScaled(change.changedAmount) <= toScaled(amountLimit)
  );
}
